import { AzureSearchBarAutocomplete } from '@/components/AzureSearchBarAutocomplete'
import Icon from '@/components/Icon'
import { Loading } from '@/components/Loading'
import { ButtonSimple } from '@/components/nsw/button/ButtonSimple'
import { NswResultBar } from '@/components/nsw/filters/NswResultBar'
import { GridCol } from '@/components/nsw/grid/GridCol'
import { GridWrapper } from '@/components/nsw/grid/GridWrapper'
import Pagination from '@/components/nsw/Pagination'
import AzureSearchResultItem from '@/components/search/AzureSearchResultItem'
import { TreeMultiSelect } from '@/components/tree-multi-select/TreeMultiSelect'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import {
	DEFAULT_NUQS_TRANSITION_OPTIONS,
	TAXO_TERM_LIFE_SKILLS,
} from '@/constants'
import { useIsInIframe } from '@/hooks/useIframe'
import { useResourceTypeTreeNodeProps } from '@/hooks/useResourceTypeTreeNodeProps'
import { useToggle } from '@/hooks/useToggle'
import { useTreeMultiSelectSelected } from '@/hooks/useTreeMultiSelectSelected'
import { AceGroup, Syllabus, WpSearch } from '@/kontent/content-types'
import { contentTypes } from '@/kontent/project/contentTypes'
import { taxonomies as projectTaxonomies } from '@/kontent/project/taxonomies'
import { TaxoKeyLearningArea, TaxoResourceType } from '@/kontent/taxonomies'
import { UrlLink } from '@/legacy-ported/utilities/frontendTypes'
import {
	getItemByCodename,
	getSiteMappings,
	loadWebsiteConfig,
} from '@/lib/api'
import { getAllItemsByType } from '@/lib/getAllItemsByType'
import {
	CommonPageProps,
	KontentCurriculumCommonResultData,
	TaxoStageWithLifeSkill,
} from '@/types'
import { NesaSearchResult } from '@/types/searchResult'
import {
	convertProjectModelTaxonomiesToElementModelsTaxonomyTerm,
	delay,
	isAllowPreviewExternalSyllabus,
	isShowPublished,
} from '@/utils'
import {
	useAllSyllabusFromAzureSearch,
	useSyllabusAzureSearch,
} from '@/utils/azure-search/useSyllabusAzureSearch'
import { cleanJson } from '@/utils/cleanJson'
import {
	useAppInsightsContext,
	useTrackEvent,
} from '@microsoft/applicationinsights-react-js'
import animateScrollTo from 'animated-scroll-to'
import clsx from 'clsx'
import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { queryTypes, useQueryStates } from 'next-usequerystate'
import Head from 'next/head'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const _fnHandleCheckboxChange =
	(stage, state, setState, beforeChangeFn) => (_e) => {
		if (beforeChangeFn) {
			beforeChangeFn(stage, state, setState)
		}

		// toggle or not based on qStage. qStage is an array of string
		const newValues = state ? state.split(',') : []
		const index = newValues.indexOf(stage)

		if (index === -1) {
			newValues.push(stage)
		} else {
			newValues.splice(index, 1)
		}
		setState(newValues.join(','), DEFAULT_NUQS_TRANSITION_OPTIONS)
	}

type CommaSeparateString = string

export const getFilterString = (
	valueString: CommaSeparateString,
	fieldName: string,
	delimiter = ',',
	fieldType = 'collection',
	isNegative = false,
) => {
	if (fieldType === 'collection') {
		return `${fieldName}/any(g: search.in(g, '${valueString}', '${delimiter}'))`
	}
	if (fieldType === 'string') {
		return isNegative
			? `not search.in(${fieldName}, '${valueString}', '${delimiter}')`
			: `search.in(${fieldName}, '${valueString}', '${delimiter}')`
	}
}

const fnMapQueryToTreeNode = (query: string) => {
	return {
		value: query,
		label: query,
	}
}

const STYLE_TREE_MULTI_SELECT = {
	'i, .radio-item': {
		display: 'none',
	},
}

export default function SearchContainer({
	searchBarPlaceholder = 'Search',
	azureSearchOptions = {},
	defaultContentType = '',
	isUseWildcard = false,
	isExcludeExternalFromResult = true,
	hidePagination = false,
	showTitleOnlyForResults = false,
	redirectWhenIsNotInIframe = '/search',
	includeQueryParamsWhenRedirectingWhenIsNotInIframe = true,
	hideResultsBar = false,
	resultsWrapperClassName = '',
	preserveFiltersOnSearch = false,
	enableSearchOnChange = false,
	sortResultsAlphabetically = false,
	onlyRefreshResult = false,
	...props
}: CommonPageProps<
	WpSearch,
	KontentCurriculumCommonResultData<WpSearch> & {
		aceGroups: AceGroup[]
	}
> & {
	searchBarPlaceholder?: string
	azureSearchOptions?: {
		contentType?: string
		searchFields?: string
	}
	defaultContentType?: string
	isUseWildcard?: boolean
	isExcludeExternalFromResult?: boolean
	hidePagination?: boolean
	showTitleOnlyForResults?: boolean
	/** If the page is not in an iframe, redirect to this page */
	redirectWhenIsNotInIframe?: string
	/** If the page is not in an iframe, include the query params in the redirect url */
	includeQueryParamsWhenRedirectingWhenIsNotInIframe?: boolean
	/** If true, the results bar will not be shown */
	hideResultsBar?: boolean
	/** Class name for the results wrapper */
	resultsWrapperClassName?: string
	/** If true, preserve kla and stage filters when searching */
	preserveFiltersOnSearch?: boolean
	/** If true, search updates as user types (real-time search) */
	enableSearchOnChange?: boolean
	/** If true, sort results alphabetically by title */
	sortResultsAlphabetically?: boolean
	/** If true, only refresh search results without updating URL (prevents iframe reload in side-by-side view) */
	onlyRefreshResult?: boolean
}) {
	const appInsights = useAppInsightsContext()
	const refScrollHereAfterFilter = useRef<HTMLDivElement>(null)
	const refFirstLoad = useRef(true)
	const [searchBarKey, setSearchBarKey] = useState(0)
	const refSearchDebounceTimeout = useRef<ReturnType<
		typeof setTimeout
	> | null>(null)
	const { data: propsData } = props
	const { keyLearningAreas, stages } = propsData || {}
	const isInIframe = useIsInIframe(true)

	// Local state for search query and filters when onlyRefreshResult is true (prevents URL change and iframe reload)
	const [localSearchQuery, setLocalSearchQuery] = useState<string | null>(
		null,
	)
	const [localKla, setLocalKla] = useState<string | null>(null)
	const [localStage, setLocalStage] = useState<string | null>(null)

	const [queries, setQueries] = useQueryStates({
		q: queryTypes.string,
		p: queryTypes.integer.withDefault(1),
		ps: queryTypes.integer.withDefault(10),
		s: queryTypes.string,
		kla: queryTypes.string,
		st: queryTypes.string,
		rt: queryTypes.string, //resource type
		ace: queryTypes.string, //ace
		ct: queryTypes.string.withDefault(defaultContentType), //content type default from props
	})

	const {
		q,
		p,
		ps: pageSize,
		s: qSyllabus,
		kla: qKla,
		st: qStage,
		rt: qResourceType,
		ace: qAce,
	} = queries

	// Use local state for search query and filters when onlyRefreshResult is true, otherwise use URL query params
	const effectiveSearchQuery = onlyRefreshResult ? localSearchQuery : q
	const effectiveKla = onlyRefreshResult ? localKla : qKla
	const effectiveStage = onlyRefreshResult ? localStage : qStage

	const selectedQKla = useMemo(
		() => (effectiveKla ? effectiveKla.split(',') : []),
		[effectiveKla],
	)
	const _selectedQSyllabus = useMemo(
		() => (qSyllabus ? qSyllabus.split(',') : []),
		[qSyllabus],
	)
	const selectedQStages = useMemo(
		() => (effectiveStage ? effectiveStage.split(',') : []),
		[effectiveStage],
	)
	const selectedQsResourceType = useMemo(
		() => (queries.rt ? queries.rt.split(',') : []),
		[queries.rt],
	)
	const selectedQAce = useMemo(
		() => (queries.ace ? queries.ace.split(',') : []),
		[queries.ace],
	)

	type ComputedFilterObject = {
		kla: string | null
		syllabus: string | null
		stage: string | null
		resourceType: string | null
		ace: string | null
		contentType: string | null
		stageWithSyllabusContentTypes: string | null
		isExternal?: string | null
	}

	const computedFilterObjects = useMemo<ComputedFilterObject>(() => {
		const filter: ComputedFilterObject = {
			kla: null,
			syllabus: null,
			stage: null,
			resourceType: null,
			ace: null,
			contentType: null,
			stageWithSyllabusContentTypes: null,
			isExternal: null,
		}

		if (effectiveKla) {
			filter.kla = getFilterString(effectiveKla, 'kla_names')
		}

		if (qSyllabus) {
			filter.syllabus = getFilterString(qSyllabus, 'syllabuse_codes')
		}

		if (effectiveStage) {
			const qStageSplit = effectiveStage.split(',')
			const stageCodes = qStageSplit.flatMap((s) => {
				return s.replace('life_skills@', '').split('___')
			})

			filter.stage = getFilterString(stageCodes.join(','), 'stage_codes')

			const stageWithSyllabusContentTypes = qStageSplit
				.flatMap((s) => {
					if (s.includes('@')) {
						const sylContentType = s.split('@')[0]
						const stageCodes = s
							.replace('life_skills@', '')
							.split('___')

						return `(stage_codes/any(g: search.in(g, '${stageCodes.join(
							',',
						)}', ',')) and syllabus_content_types/any(g: g eq '${sylContentType}'))`
					}
					return `stage_codes/any(g: g eq '${s}')`
				})
				.join(' or ')

			filter.stageWithSyllabusContentTypes = stageWithSyllabusContentTypes
		}

		if (qResourceType) {
			filter.resourceType = getFilterString(
				qResourceType,
				'resource_type',
				',',
				'string',
			)
		}
		if (qAce) {
			filter.ace = getFilterString(qAce, 'ace_group_codes')
		}

		// Set contentType filter based on prop
		filter.contentType = defaultContentType
			? `content_type eq '${defaultContentType}'`
			: getFilterString(
					'syllabus-resource,glossary,ace-subgroup,change-log,web-page',
					'content_type',
					',',
					'string',
					true,
			  )

		if (isExcludeExternalFromResult) {
			filter.isExternal = 'is_external eq false'
		}

		return filter
	}, [
		effectiveKla,
		qSyllabus,
		effectiveStage,
		qResourceType,
		qAce,
		defaultContentType,
		isExcludeExternalFromResult,
	])

	const computedFilter = useMemo(() => {
		const filter = []

		if (effectiveKla) {
			filter.push(computedFilterObjects.kla)
		}

		if (qSyllabus) {
			filter.push(computedFilterObjects.syllabus)
		}

		if (effectiveStage) {
			filter.push(computedFilterObjects.stageWithSyllabusContentTypes)
		}
		if (qResourceType) {
			filter.push(computedFilterObjects.resourceType)
		}
		if (qAce) {
			filter.push(computedFilterObjects.ace)
		}

		if (computedFilterObjects.contentType) {
			filter.push(computedFilterObjects.contentType)
		}

		if (computedFilterObjects.isExternal) {
			filter.push(computedFilterObjects.isExternal)
		}

		return filter.join(' and ')
	}, [
		computedFilterObjects,
		effectiveKla,
		qSyllabus,
		effectiveStage,
		qResourceType,
		qAce,
	])

	const maxSkip = 100000
	const effectivePageSize = hidePagination ? maxSkip : pageSize

	const { data, isFetched, isFetching } = useSyllabusAzureSearch({
		q: `${effectiveSearchQuery || ''}${isUseWildcard ? '*' : ''}`,
		page: p,
		pageSize: effectivePageSize,
		filter: computedFilter,
		...azureSearchOptions,
	})

	const trackSearch = useTrackEvent(appInsights, 'Search Syllabus', {
		searchText: effectiveSearchQuery,
		learningAreasFilter: effectiveKla,
		syllabusesFilter: qSyllabus,
		stagesFilter: effectiveStage,
		resourceTypesFilter: qResourceType,
		aceRulesFilter: qAce,
		contentTypesFilter: defaultContentType,
		pageSize: effectivePageSize,
		page: p,
		totalCount: data?.json.count,
	})

	let results = data?.json

	// Sort results alphabetically by title if the prop is enabled
	if (sortResultsAlphabetically && results?.value) {
		results = {
			...results,
			value: [...results.value].sort((a, b) => {
				const titleA = a.title?.toLowerCase() || ''
				const titleB = b.title?.toLowerCase() || ''
				return titleA.localeCompare(titleB)
			}),
		}
	}

	const count = results?.count || 0
	const maxPage =
		count > 0
			? Math.ceil(count / effectivePageSize)
			: Math.ceil(maxSkip / effectivePageSize) + 1
	const [isFilterShown, toggleIsFilterShown] = useToggle(true)
	const refSearchTop = useRef<HTMLDivElement>(null)

	const getCommonAdditionalFnOnDropdownChange = useCallback(
		(queryKey: string, isParent?: boolean) =>
			(_selectedNodes, _selectedChildNodes) => {
				setQueries(
					{
						[queryKey]:
							(isParent ? _selectedNodes : _selectedChildNodes)
								.map((node) => node.value)
								.join(',') || null,
						p: null,
						ct: defaultContentType,
					},
					DEFAULT_NUQS_TRANSITION_OPTIONS,
				)
			},
		[setQueries, defaultContentType],
	)

	const klaOptions = useMemo(() => {
		return keyLearningAreas
			.filter((kla) => {
				// Hide "Multiple" - backend configuration value not for user selection
				if (kla.name === 'Multiple') {
					return false
				}
				// Hide VET when in iframe (side-by-side mode)
				if (isInIframe && kla.name === 'VET') {
					return false
				}
				return true
			})
			.map((kla) => {
				return {
					label: kla.name,
					value: kla.name,
					count:
						results?.facets?.kla_names.find(
							(klaFacet) => klaFacet.value === kla.name,
						)?.count || 0,
				}
			})
	}, [keyLearningAreas, results, isInIframe])

	const syllabusOptionsFromSearchFacet = useAllSyllabusFromAzureSearch()

	const _syllabusOptions = useMemo(() => {
		return syllabusOptionsFromSearchFacet
			.map((syllabus) => {
				return {
					label: syllabus.label,
					value: syllabus.value,
					count:
						results?.facets?.syllabuse_codes.find(
							(syllabusFacet) =>
								syllabusFacet.value === syllabus.value,
						)?.count || 0,
				}
			})
			.sort((a, b) => b.count - a.count)
	}, [syllabusOptionsFromSearchFacet, results])

	const stageOptions = useMemo<
		{
			label: string
			value: string
			count: number
			hideCount?: boolean
		}[]
	>(() => {
		return stages
			.filter((stage) => stage.codename !== 'life_skills')
			.flatMap((stage) => {
				let _label = stage.name
				//@ts-ignore
				if (stage.codename === 'life_skills@stage_4___stage_5') {
					_label = 'Life Skills (7–10)'
				}
				//@ts-ignore
				if (stage.codename === 'life_skills@stage_6') {
					_label = 'Life Skills (11–12)'
				}

				if (
					stage.codename === 'stage_5' ||
					stage.codename === 'stage_6'
				) {
					const lifeSkillStageCodename =
						stage.codename === 'stage_5'
							? 'life_skills@stage_4___stage_5'
							: 'life_skills@stage_6'

					const lifeSkillStageCount =
						results?.facets?.stage_codes.find(
							(stageFacet) =>
								stageFacet.value === lifeSkillStageCodename,
						)

					return [
						{
							label: stage.name,
							value: stage.codename as string,
							count:
								results?.facets?.stage_codes.find(
									(stageFacet) =>
										stageFacet.value === stage.codename,
								)?.count || 0,
						},
						{
							label:
								stage.codename === 'stage_5'
									? 'Life Skills (7–10)'
									: 'Life Skills (11–12)',
							value: lifeSkillStageCodename,
							count: lifeSkillStageCount?.count || 0,
						},
					]
				}
				return [
					{
						label: stage.name,
						value: stage.codename,
						count:
							results?.facets?.stage_codes.find(
								(stageFacet) =>
									stageFacet.value === stage.codename,
							)?.count || 0,
					},
				]
			})
			.filter((option) => {
				// feat(OC2-3397): Hide Life Secondary from filter in Side by Side
				if (
					isInIframe &&
					isUseWildcard &&
					option.label === 'Life Skills (7–10)'
				) {
					return false
				}
				return true
			})
	}, [stages, results, isInIframe, isUseWildcard])

	const resourceTypeOptions = useResourceTypeTreeNodeProps({
		selectedResourceTypes: selectedQsResourceType.filter(
			Boolean,
		) as TaxoResourceType[],
		isUseResourceTypeName: true,
		additionalResourceTypeOptions: [
			{
				name: 'Glossary',
				value: 'glossary' as TaxoResourceType,
			},
		],
		putOtherAtTheEnd: true,
		isSortByLabel: true,
	})

	const filteredResourceTypeOptions = useMemo(
		() =>
			resourceTypeOptions.map((resourceType) => {
				return {
					...resourceType,
					label: `${resourceType.label} (${
						results?.facets?.resource_type.find(
							(resourceTypeFacet) =>
								resourceTypeFacet.value === resourceType.value,
						)?.count || 0
					})`,
				}
			}),
		[resourceTypeOptions, results?.facets?.resource_type],
	)

	const selectedResourceTypeOptionNodes = filteredResourceTypeOptions
		.map((resourceType) => {
			return resourceType.value
		})
		.filter((resourceType) =>
			selectedQsResourceType.includes(resourceType as TaxoResourceType),
		)

	const { handleRemoveNode: _handleRemoveNodeKla } =
		useTreeMultiSelectSelected<TaxoKeyLearningArea>({
			initialSelectedNodes: selectedQKla.map(fnMapQueryToTreeNode),
			additionalFnOnDropdownChange: getCommonAdditionalFnOnDropdownChange(
				'kla',
				true,
			),
		})

	useTreeMultiSelectSelected<TaxoStageWithLifeSkill>({
		initialSelectedNodes: selectedQStages.map(fnMapQueryToTreeNode),
		additionalFnOnDropdownChange: getCommonAdditionalFnOnDropdownChange(
			'st',
			true,
		),
	})

	useTreeMultiSelectSelected<TaxoResourceType>({
		initialSelectedNodes: selectedResourceTypeOptionNodes,
		additionalFnOnDropdownChange: getCommonAdditionalFnOnDropdownChange(
			'rt',
			true,
		),
	})

	useTreeMultiSelectSelected({
		initialSelectedChildNodes: selectedQAce.map(fnMapQueryToTreeNode),
		additionalFnOnDropdownChange:
			getCommonAdditionalFnOnDropdownChange('ace'),
	})

	const scrollToTopOfRightCol = useCallback(() => {
		if (refScrollHereAfterFilter.current) {
			animateScrollTo(refScrollHereAfterFilter.current, {
				speed: 0,
				verticalOffset: -24,
			})
		}
	}, [])

	const resetQueryParams = useCallback(
		(text?: string) => {
			return {
				q: text || null,
				p: null,
				ps: null,
				s: null,
				kla: null,
				st: null,
				rt: null,
				ace: null,
				ct: defaultContentType,
			}
		},
		[defaultContentType],
	)

	const handleSearch = useCallback(
		async (text: string) => {
			if (!text) {
				setSearchBarKey((prev) => prev + 1)
				// focus to the search bar
				await delay(100)
				refSearchTop.current?.querySelector('input')?.focus()
			}

			if (onlyRefreshResult) {
				// Update local state only - no URL change (prevents iframe reload)
				setLocalSearchQuery(text || null)
			} else {
				const resetQueryParamsResult = resetQueryParams(text)
				// don't reset the `ps` (page size)
				// if preserveFiltersOnSearch is true, also don't reset `kla` and `st`
				const { ps, ..._rest } = resetQueryParamsResult
				const rest = preserveFiltersOnSearch
					? (() => {
							const { kla, st, ...__rest } = _rest
							return __rest
					  })()
					: _rest
				setQueries(
					{ ...rest, ct: defaultContentType },
					DEFAULT_NUQS_TRANSITION_OPTIONS,
				)
			}
			scrollToTopOfRightCol()
		},
		[
			resetQueryParams,
			setQueries,
			scrollToTopOfRightCol,
			defaultContentType,
			preserveFiltersOnSearch,
			onlyRefreshResult,
		],
	)

	const handleReset = useCallback(
		async (text = '') => {
			if (onlyRefreshResult) {
				// Clear local state only - no URL change (prevents iframe reload)
				setLocalSearchQuery(null)
				setLocalKla(null)
				setLocalStage(null)
			} else {
				const resetQueryParamsResult = resetQueryParams(text)
				// don't reset the `ps` (page size) and `q` (search text)
				const { ps, q, ...rest } = resetQueryParamsResult
				setQueries(
					{ ...rest, ct: defaultContentType },
					DEFAULT_NUQS_TRANSITION_OPTIONS,
				)
			}
			refFirstLoad.current = true
			setSearchBarKey((prev) => prev + 1)

			// focus to the search bar
			await delay(100)
			refSearchTop.current?.querySelector('input')?.focus()
		},
		[resetQueryParams, setQueries, defaultContentType, onlyRefreshResult],
	)

	const handleSearchChange = useCallback(
		(text: string) => {
			// Clear existing timeout
			if (refSearchDebounceTimeout.current) {
				clearTimeout(refSearchDebounceTimeout.current)
			}

			// Set new timeout to delay search execution
			refSearchDebounceTimeout.current = setTimeout(() => {
				if (onlyRefreshResult) {
					// Update local state only - no URL change (prevents iframe reload)
					setLocalSearchQuery(text || null)
				} else {
					// Original behavior - update URL
					setQueries(
						{
							q: text || null,
							p: null, // Reset page to 1 when search changes
						},
						DEFAULT_NUQS_TRANSITION_OPTIONS,
					)
				}
			}, 1000) // 1000ms delay
		},
		[setQueries, onlyRefreshResult],
	)

	useEffect(() => {
		if (isFetched && !refFirstLoad.current) {
			scrollToTopOfRightCol()
		}
		if (isFetched) {
			refFirstLoad.current = false
		}
	}, [isFetched, scrollToTopOfRightCol])

	// Cleanup search debounce timeout on unmount
	useEffect(() => {
		return () => {
			if (refSearchDebounceTimeout.current) {
				clearTimeout(refSearchDebounceTimeout.current)
			}
		}
	}, [])

	useEffect(() => {
		if (isFetched) {
			trackSearch({
				searchText: effectiveSearchQuery,
				learningAreasFilter: effectiveKla,
				syllabusesFilter: qSyllabus,
				stagesFilter: effectiveStage,
				resourceTypesFilter: qResourceType,
				aceRulesFilter: qAce,
				contentTypesFilter: defaultContentType,
				pageSize: effectivePageSize,
				page: p,
				totalCount: results?.count || 0,
			})
		}
	}, [
		effectiveSearchQuery,
		effectiveKla,
		qSyllabus,
		effectiveStage,
		qResourceType,
		qAce,
		isFetched,
		pageSize,
		hidePagination,
		p,
		results?.count,
		defaultContentType,
		trackSearch,
		effectivePageSize,
	])

	useEffect(() => {
		if (!isInIframe) {
			if (includeQueryParamsWhenRedirectingWhenIsNotInIframe) {
				window.location.href = `${redirectWhenIsNotInIframe}${window.location.search}`
				return
			}

			window.location.href = redirectWhenIsNotInIframe

			return
		}
	}, [
		isInIframe,
		includeQueryParamsWhenRedirectingWhenIsNotInIframe,
		redirectWhenIsNotInIframe,
	])

	if (!isInIframe) {
		return null // Return null while redirecting
	}

	return (
		<>
			<Head>
				<link key="canonical" rel="canonical" href="/search-syllabus" />
				<meta key="robots" name="robots" content="noindex,nofollow" />
			</Head>
			<div
				className={clsx('nsw-container', {
					'pt-4': isInIframe,
					'pt-8': !isInIframe,
				})}
				ref={refScrollHereAfterFilter}
			>
				{!isInIframe && (
					<CommonCopyUrlWrapper
						url="/search-syllabus"
						className="mb-8"
					>
						<h1 className="mb-8">Search</h1>
					</CommonCopyUrlWrapper>
				)}

				<div className="mb-8" ref={refSearchTop}>
					<GridWrapper
						className={clsx('!mt-0', {
							'lg:opacity-0': isFetching,
						})}
					>
						<GridCol lg={12} className="!pt-0">
							<div className="grid grid-cols-2 gap-4">
								<div className="col-span-2">
									<AzureSearchBarAutocomplete
										key={searchBarKey}
										initialSearchText={
											effectiveSearchQuery || ''
										}
										placeholder={searchBarPlaceholder}
										handleSearchPress={handleSearch}
										autoFocus={!effectiveSearchQuery}
										disableAutocomplete
										handleSearchChange={
											enableSearchOnChange
												? handleSearchChange
												: undefined
										}
									/>
								</div>
								{((effectiveSearchQuery && isInIframe) ||
									isUseWildcard) && (
									<>
										<TreeMultiSelect
											css={STYLE_TREE_MULTI_SELECT}
											data={klaOptions.map((node) => ({
												...node,
												checked:
													node.value === effectiveKla,
											}))}
											onChange={(selectedNode) => {
												const newValue =
													selectedNode?.value ===
													effectiveKla
														? null
														: selectedNode?.value ||
														  ''
												if (onlyRefreshResult) {
													// Update local state only - no URL change (prevents iframe reload)
													setLocalKla(newValue)
												} else {
													setQueries(
														{
															kla: newValue,
															p: null,
														},
														DEFAULT_NUQS_TRANSITION_OPTIONS,
													)
												}
											}}
											placeholder="All key learning areas"
											texts={{
												label: 'All key learning areas',
											}}
											mode="radioSelect"
											keepTreeOnSearch
											showDropdown="default"
										/>
										<TreeMultiSelect
											css={STYLE_TREE_MULTI_SELECT}
											data={stageOptions.map((node) => ({
												...node,
												checked:
													node.value ===
													effectiveStage,
											}))}
											onChange={(selectedNode) => {
												const newValue =
													selectedNode?.value ===
													effectiveStage
														? null
														: selectedNode?.value ||
														  ''
												if (onlyRefreshResult) {
													// Update local state only - no URL change (prevents iframe reload)
													setLocalStage(newValue)
												} else {
													setQueries(
														{
															st: newValue,
															p: null,
														},
														DEFAULT_NUQS_TRANSITION_OPTIONS,
													)
												}
											}}
											placeholder="All stages of learning"
											texts={{
												label: 'All stages of learning',
											}}
											mode="radioSelect"
											keepTreeOnSearch
											showDropdown="default"
										/>
									</>
								)}
							</div>
						</GridCol>
						<GridCol
							lg={12}
							className={clsx('!pt-0', {
								hidden: isUseWildcard
									? false
									: !effectiveSearchQuery,
							})}
						>
							{isFetching && (
								<div className="bg-white/90 w-full flex justify-center pt-6">
									<Loading />
								</div>
							)}
							<NswResultBar
								className={clsx('!pt-6', {
									hidden:
										hideResultsBar ||
										(isUseWildcard &&
											!effectiveSearchQuery) ||
										isFetching,
								})}
								css={{
									'&&': {
										paddingTop: '.875rem',
										paddingBottom: '.875rem',
										marginTop: '2rem',
										marginBottom: '2rem',
										alignItems: 'center',
									},
									'.NswResultBar__result': {
										display: 'flex',
										gap: '1rem',
										//add opacity:0 if no results
										opacity: results?.count ? 1 : 0,
									},
									'.nsw-results-bar__sorting': {
										paddingTop: 0,
										width: 1,
										overflow: 'hidden',
									},
									'.nsw-results-bar__info': {
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										gap: '1rem',
										width: '100%',
									},
								}}
								page={p}
								pageSize={effectivePageSize}
								total={results?.count || 0}
								slotShowingResultsNumberBefore={
									!isFilterShown && (
										<ButtonSimple
											className="border-r border-r-nsw-grey-03 pr-4"
											onClick={toggleIsFilterShown}
											slotBeforeChildren={
												<Icon
													icon="mdi:tune"
													width={24}
													height={24}
													className="block p-0.5 bg-nsw-brand-dark text-white rounded-[4px]"
												/>
											}
										>
											Show filters
										</ButtonSimple>
									)
								}
							></NswResultBar>

							<div
								className={clsx(
									'mb-4',
									{
										hidden: isUseWildcard
											? isFetching
											: false,
									},
									[resultsWrapperClassName],
								)}
							>
								<div className="divide-y divide-nsw-grey-04">
									{results?.value?.map(
										(document: NesaSearchResult) => {
											return (
												<AzureSearchResultItem
													key={document.content_id}
													document={document}
													showTitleOnly={
														showTitleOnlyForResults
													}
												/>
											)
										},
									)}
								</div>
							</div>
							{!results?.value?.length &&
								(!isUseWildcard ||
									(isUseWildcard && !isFetching)) && (
									<h4 className="text-center mt-20">
										{"We didn't find any results. "}
										<button
											type="reset"
											className="underline bold nsw-text--brand-dark"
											onClick={() => handleReset()}
										>
											Clear all filters
										</button>
									</h4>
								)}
						</GridCol>
						<GridCol
							className={clsx({
								hidden: isUseWildcard ? isFetching : false,
							})}
						>
							{results?.count > effectivePageSize && (
								<Pagination
									page={p}
									count={maxPage}
									onChange={(_, pageNumber) => {
										setQueries(
											{
												p: pageNumber,
											},
											DEFAULT_NUQS_TRANSITION_OPTIONS,
										)
										animateScrollTo(
											refScrollHereAfterFilter.current,
											{
												speed: 0,
											},
										)
									}}
								></Pagination>
							)}
						</GridCol>
					</GridWrapper>
				</div>
			</div>
		</>
	)
}

export const getStaticProps: GetStaticProps<
	CommonPageProps<any, any> & { breadcrumbLinks: UrlLink[] }
> = async ({ preview = false, previewData = {}, params }) => {
	const _isAllowPreviewExternalSyllabus = isAllowPreviewExternalSyllabus()
	const isGetPreviewContent = !isShowPublished(previewData) && preview

	let [config, mappings, syllabuses, aceGroups, searchPage] =
		await Promise.all([
			loadWebsiteConfig(isGetPreviewContent),
			getSiteMappings(isGetPreviewContent),
			getAllItemsByType<Syllabus>({
				type: contentTypes.syllabus.codename,
				depth: 0,
				preview,
				elementsParameter: [
					contentTypes.syllabus.elements.title.codename,
					contentTypes.syllabus.elements.key_learning_area__items
						.codename,
					contentTypes.syllabus.elements.stages__stages.codename,
					contentTypes.syllabus.elements.syllabus.codename,
					contentTypes.syllabus.elements.code.codename,
					contentTypes.syllabus.elements.redirecturl.codename,
					contentTypes.syllabus.elements.doredirect.codename,
					contentTypes.syllabus.elements.implementation_title
						.codename,
					contentTypes.syllabus.elements.implementation_info.codename,
					contentTypes.syllabus.elements.web_content_rtb__content
						.codename,
					contentTypes.syllabus.elements.syllabus_type__items
						.codename,
					contentTypes.syllabus.elements.allowpreview.codename,
					contentTypes.syllabus.elements.languages.codename,
				],
				allFilter: _isAllowPreviewExternalSyllabus
					? null
					: {
							element: `elements.${contentTypes.syllabus.elements.doredirect.codename}`,
							value: ['no'],
					  },
			}),
			getAllItemsByType<AceGroup>({
				type: contentTypes.ace_group.codename,
				depth: 0,
				preview,
				elementsParameter: [
					contentTypes.ace_group.elements.title.codename,
					contentTypes.ace_group.elements.code.codename,
				],
			}),
			getItemByCodename<WpSearch>({
				codename: 'search',
				depth: 0,
				preview,
			}),
		])

	/** This breadcrumbLinks is to be passed to NavPage for breadcrumb to work */
	const breadcrumbLinks: UrlLink[] = []

	const _props: InferGetStaticPropsType<typeof getStaticProps> = {
		mappings,
		preview,
		previewData,
		data: {
			config,
			stages: null,
			stageGroups: null,
			keyLearningAreas: null,
			assets: null,
			glossaries: null,
			syllabuses: null,
			pageResponse: searchPage,
		},
		params: {
			...params,
			pageTitle: 'Custom syllabus view',
			isCanonical: true,
			slug: [],
		},
		breadcrumbLinks,
	}

	_props.data.stages = [
		...convertProjectModelTaxonomiesToElementModelsTaxonomyTerm(
			projectTaxonomies.stage,
		),
		TAXO_TERM_LIFE_SKILLS,
	]
	_props.data.stageGroups =
		convertProjectModelTaxonomiesToElementModelsTaxonomyTerm(
			projectTaxonomies.stage_group,
		)
	_props.data.keyLearningAreas =
		convertProjectModelTaxonomiesToElementModelsTaxonomyTerm(
			projectTaxonomies.key_learning_area,
		)
	_props.data.assets = []
	_props.data.syllabuses = syllabuses
	_props.data.glossaries = []
	_props.rootLayoutClassName = 'max-w-none mx-0 px-0 !pt-0'
	_props.data.aceGroups = aceGroups.items

	return {
		props: {
			...cleanJson(_props),
		},
	}
}
